import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Input, Toggle } from '../../components/ui/Input'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { formatDate } from '../../lib/utils'
import type { ConfiguracaoPluggyAdmin } from '../../types'

const MENSAGEM_PADRAO = 'A conexão com o banco está temporariamente indisponível.'
const MAX_PADRAO = 200

/** Admin: interruptor geral da conexão com o banco (Pluggy) e a mensagem mostrada quando ela está desligada. */
export function PluggyConfigPage() {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['admin-pluggy'], queryFn: adminApi.pluggyConfiguracao })
  return (
    <>
      <PageHeader title="Pluggy" />
      {isLoading ? <Card><LoadingState /></Card> : isError || !data ? <Card><ErrorState message={(error as Error)?.message || 'Não foi possível carregar a configuração.'} /></Card> : <Formulario key={data.atualizadoEm ?? 'inicial'} config={data} />}
    </>
  )
}

function Formulario({ config }: { config: ConfiguracaoPluggyAdmin }) {
  const qc = useQueryClient()
  const toast = useToast()
  const padrao = config.mensagemPadrao || MENSAGEM_PADRAO
  const max = config.mensagemMaxCaracteres ?? MAX_PADRAO
  const [ativa, setAtiva] = useState(config.ativa)
  // Mensagem em branco = padrão; só mostramos no campo o que foi personalizado.
  const [mensagem, setMensagem] = useState(config.mensagem && config.mensagem !== padrao ? config.mensagem : '')

  const erro = mensagem.length > max ? `No máximo ${max} caracteres.` : undefined

  const salvar = useMutation({
    mutationFn: () => adminApi.salvarPluggyConfiguracao({ ativa, mensagem: mensagem.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-pluggy'] })
      qc.invalidateQueries({ queryKey: ['pluggy-status'] })
      toast.success('Configuração da Pluggy salva.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Card>
      <CardHeader title="Conexão com o banco" icon="ri-bank-line" />
      <CardBody className="space-y-5">
        <p className="text-13 text-muted">
          Desligue se a Pluggy pedir para retirar o recurso. Os dados já importados continuam disponíveis e o app funciona sem a conexão.
        </p>
        <div className="flex items-center justify-between gap-4 rounded bg-surface2 px-4 py-3">
          <div>
            <p className="font-medium text-white">Conexão com o banco (Pluggy) ligada</p>
            <p className="text-2xs text-muted">
              Desligada, ninguém conecta, atualiza nem sincroniza banco. Vínculos, pendências e o desvincular continuam funcionando.
            </p>
          </div>
          <Toggle label="" checked={ativa} onChange={setAtiva} disabled={salvar.isPending} />
        </div>
        <div>
          <Input
            label="Mensagem mostrada quando estiver desligada"
            value={mensagem} error={erro} placeholder={padrao}
            onChange={(e) => setMensagem(e.target.value)}
          />
          <div className="mt-1 flex justify-between gap-3 text-2xs text-muted">
            <span>Em branco, vale a mensagem padrão.</span>
            <span className={mensagem.length > max ? 'text-expense' : undefined}>{mensagem.length}/{max}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-2xs text-muted">{config.atualizadoEm ? `Última alteração em ${formatDate(config.atualizadoEm)}.` : 'Ainda não alterado (ligada).'}</p>
          <Button icon="ri-save-line" disabled={!!erro} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
        </div>
      </CardBody>
    </Card>
  )
}
